import React from 'react';
import { InterestRateTooltip, MaturityTooltip, FundingWindowTooltip } from '../Tooltips';
import { LARGE_BREAKPOINT_WIDTH } from '../../constants';
import { productTypes } from '../../../../utils/productHelpers';

/** @import { TooltipPosition } from '../../DashboardNewTypes' */

/**
 * Global Tooltips component
 * Renders fixed-position tooltips at the root level for desktop view
 * @param {{
 *   accounts: any[];
 *   openTooltipId: string | null;
 *   popupPosition: TooltipPosition | null;
 *   openMaturityTooltipId: string | null;
 *   maturityPopupPosition: TooltipPosition | null;
 *   openFundingTooltipId: string | null;
 *   fundingPopupPosition: TooltipPosition | null;
 *   openRateChangeTooltipId: string | null;
 *    rateChangePopupPosition: TooltipPosition | null;
 *   featureEnabled: (flag: string) => boolean;
 * }} props
 */
const GlobalTooltips = ({
    accounts,
    openTooltipId,
    popupPosition,
    openMaturityTooltipId,
    maturityPopupPosition,
    openFundingTooltipId,
    fundingPopupPosition,
    openRateChangeTooltipId,
    rateChangePopupPosition,
    featureEnabled
}) => {
    const isDesktop = window.innerWidth >= LARGE_BREAKPOINT_WIDTH;
    
    if (!isDesktop) {
        return null;
    }
    
    const getTooltipStyle = (/** @type {TooltipPosition} */ position) => ({
        top: `${position.top}px`,
        left: position.left !== undefined ? `${position.left}px` : 'auto',
        right: position.right !== undefined ? `${position.right}px` : 'auto',
        width: `${position.width}px`,
        '--arrow-left': position.arrowLeft !== undefined ? `${position.arrowLeft}px` : '16px'
    });

    return (
        <>
            {/* Interest Rate Tooltip */}
            {openTooltipId && popupPosition && !openTooltipId.endsWith('-available') && (
                <div 
                    className="interest-rate-tooltip"
                    style={getTooltipStyle(popupPosition)}
                >
                    {(() => {
                        const account = accounts.find((/** @type {any} */ acc) => acc.id === openTooltipId);
                        return account ? <InterestRateTooltip account={account} /> : null;
                    })()}
                </div>
            )}
            
            {/* Maturity Tooltip */}
            {openMaturityTooltipId && maturityPopupPosition && (
                <div 
                    className="maturity-info-tooltip"
                    style={getTooltipStyle(maturityPopupPosition)}
                >
                    {(() => {
                        const account = accounts.find((/** @type {any} */ acc) => acc.id === openMaturityTooltipId);
                        return (
                            <MaturityTooltip
                                isFisa={account?.product?.productType === productTypes.FixedRateCashISA}
                                enableUpdatedFisaCopy={featureEnabled('EnableDashboardMaturityCopyUpdate')}
                            />
                        );
                    })()}
                </div>
            )}
            
            {/* Funding Window Tooltip */}
            {openFundingTooltipId && fundingPopupPosition && (
                <div 
                    className="funding-info-tooltip"
                    style={getTooltipStyle(fundingPopupPosition)}
                >
                    {(() => {
                        const account = accounts.find((/** @type {any} */ acc) => acc.id === openFundingTooltipId);
                        return account ? <FundingWindowTooltip productType={account.product.productType} /> : null;
                    })()}
                </div>
            )}
            
            {/* Rate Change Tooltip */}
            {openRateChangeTooltipId && rateChangePopupPosition && (
                <div 
                    className="rate-change-info-tooltip"
                    style={getTooltipStyle(rateChangePopupPosition)}
                >
                    {(() => {
                        const account = accounts.find((/** @type {any} */ acc) => acc.id === openRateChangeTooltipId);
                        if (!account || !account.product.rateChanges) return null;
                        
                        /**
                         * @param {string} dateString - Date string to format
                         */
                        const formatRateChangeDate = (dateString) => {
                            const date = new Date(dateString);
                            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
                        };
                        
                        return (
                            <>
                                <h3>Rate change details</h3>
                                <p className="rate-change-message">
                                    You don't need to do anything, the new interest rate will be applied automatically.
                                </p>
                                {account.product.rateChanges.map((/** @type {any} */ rateChange, /** @type {number} */ index) => (
                                    <div key={index} className="rate-change-item">
                                        <div className="rate-change-details">
                                            <strong>New rate:</strong> {rateChange.aerRate.toFixed(2)}% AER* | {rateChange.rate.toFixed(2)}% gross p.a.
                                        </div>
                                        <div className="rate-change-details">
                                            <strong>Effective from:</strong> {formatRateChangeDate(rateChange.scheduledFor)}
                                        </div>
                                    </div>
                                ))}
                                <p className="rate-change-footnote">
                                    You'll earn the applicable interest rate as of the effective date specified above on your entire balance when it's between £5,000 - £250,000.
                                </p>
                            </>
                        );
                    })()}
                </div>
            )}
            
        </>
    );
};

export default GlobalTooltips;
